import { useCallback, useEffect, useRef, useMemo } from 'react';
import { TransactionCategory } from '@shipstation/financial-services-ui-components';
import axios from 'axios';
import { debounce } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { useEnableSurchargeUI } from '../../feature-flags/hooks/use-enable-surcharge-ui';
import { usePaymentAndSubscriptionMegaContext } from '../../pages/settings/payment-and-subscription/context';
import globalSelectors from '../../store/global-selectors';
import { SURCHARGE_ENROLLMENT_STATUS } from '../constants';
import { messages } from './messages';

/**
 * @typedef {import('@shipstation/financial-services-ui-components/dist/types/types/payment').TransactionCategory} TransactionCategory
 * @typedef {import('../types/fsui').SurchargeEligibility} SurchargeEligibility
 * @typedef {import('../types/fsui').Fee} Fee
 * @typedef {import('../types/fsui').Amount} Amount
 * @typedef {import('../types/fsui').TransactionPreviewResponse} TransactionPreviewResponse
 * @typedef {import('../types/fsui').PaymentSurchargePreviewResult} PaymentSurchargePreviewResult
 */

const SURCHARGE_TYPE = 'CreditCardSurcharge';
const DEBOUNCE_WAIT_MS = 750;

/**
 * Hook to check if the user is eligible for surcharge and get the surcharge percentage
 * @param {TransactionCategory} [purchaseCategory] - Category to check eligibility for
 * @returns {SurchargeEligibility} Eligibility information
 */
export const useSurchargeInfoEligibility = (purchaseCategory = TransactionCategory.AddFunds) => {
  const isSurchargeUiEnabled = useEnableSurchargeUI();
  const { paymentMethodDetails = [] } = usePaymentAndSubscriptionMegaContext();
  const accountSimple = useSelector(globalSelectors.selectAccountInformationAccountInformation);

  const surchargePercentage = useMemo(() => {
    // find the active payment method with AddFunds preference
    const paymentMethod = paymentMethodDetails.find(
      (pm) =>
        pm.assignedPreferences?.includes(purchaseCategory) && pm.fees?.some((fee) => fee.transactionCategories.includes(purchaseCategory)),
    );

    // sum all fees on that payment method, if it exists. Default to 0 if no fees found. Do not worry about the type.
    const totalSurchargePercentage =
      paymentMethod?.fees
        ?.filter((fee) => fee.transactionCategories.includes(purchaseCategory))
        .reduce((acc, fee) => acc + fee.percentage, 0) || 0;

    return totalSurchargePercentage;
  }, [paymentMethodDetails, purchaseCategory]);

  const eligibility = { isEligible: false, surchargePercentage: 0, doesSurchargeApply: false };
  if (!isSurchargeUiEnabled || !paymentMethodDetails?.length) {
    return eligibility;
  }

  eligibility.isEligible = accountSimple.surchargeEnrollmentStatus === SURCHARGE_ENROLLMENT_STATUS.Enrolled;
  eligibility.surchargePercentage = surchargePercentage; // Example: 1.9 === 1.9% surcharge
  eligibility.doesSurchargeApply = eligibility.isEligible && eligibility.surchargePercentage > 0;
  return eligibility;
};

/**
 * Makes an API call to get a transaction preview with fees, with one retry attempt
 * @param {object} args
 * @param {number} [args.requestedTransactionAmount] - The requested transaction amount
 * @returns {Promise<{data: TransactionPreviewResponse}>} Promise resolving to transaction preview data
 */
const makeTransactionPreview = async ({ requestedTransactionAmount = 0 }) => {
  const requestData = {
    requestedTransactionAmount,
  };

  try {
    return await axios.post('v2/payments/transaction_preview', requestData);
  } catch {
    // Retry once on failure
    return axios.post('v2/payments/transaction_preview', requestData);
  }
};

/**
 * Hook to get payment surcharge preview data for a given total amount
 * @param {object} args
 * @param {number} [args.total] - The base transaction amount to calculate fees for
 * @param {TransactionCategory} [args.purchaseCategory] - The transaction category for fee calculation
 * @returns {PaymentSurchargePreviewResult} Payment surcharge preview data and loading state
 */
export const usePaymentSurchargePreview = ({ total = 0, purchaseCategory = TransactionCategory.AddFunds }) => {
  const intl = useIntl();
  // the surcharge percentage here is for use _during_ checkout, and may not be accurate outside of that context
  const isSurchargeUiEnabled = useEnableSurchargeUI();
  const { doesSurchargeApply } = useSurchargeInfoEligibility(purchaseCategory);
  const [loading, setLoading] = useAsyncSafeState(true);
  const [creditCardProcessingFee, setCreditCardProcessingFee] = useAsyncSafeState(0);
  const [creditCardProcessingFeePercentage, setCreditCardProcessingFeePercentage] = useAsyncSafeState(0);
  const [totalWithFees, setTotalWithFees] = useAsyncSafeState(total);
  const [error, setError] = useAsyncSafeState('');
  const [retryCounter, setRetryCounter] = useAsyncSafeState(0);
  const [hasCheckedAtLeastOnce, setHasCheckedAtLeastOnce] = useAsyncSafeState(false);

  const triggerRetry = useCallback(() => {
    setRetryCounter((count) => count + 1);
  }, [setRetryCounter]);

  const debouncedApiCall = useRef(
    debounce((requestedTransactionAmount) => {
      makeTransactionPreview({
        requestedTransactionAmount,
      })
        .then((response) => {
          setCreditCardProcessingFee(response?.data?.totalTransactionFee?.amount || 0);
          setTotalWithFees(response?.data?.amount?.amount || requestedTransactionAmount);
          const fees = response?.data?.fees || [];
          const matchingFee = fees.find((f) => f.type === SURCHARGE_TYPE);
          if (matchingFee) {
            setCreditCardProcessingFeePercentage(matchingFee?.percentage || 0);
          }
          setLoading(false);
          setError('');
        })
        .catch((e) => {
          // we only want to block users who we think are eligible so that the majority of people are uninterrupted
          setError(doesSurchargeApply ? e.message || intl.formatMessage(messages.fallbackErrorMessage) : '');
          setLoading(false);
        });
    }, DEBOUNCE_WAIT_MS),
  );

  useEffect(() => {
    setHasCheckedAtLeastOnce(true);
    if (total === 0 || !isSurchargeUiEnabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setCreditCardProcessingFee(0);
    setCreditCardProcessingFeePercentage(0);

    debouncedApiCall.current(total);
    // retryCounter triggers a manual retry when the user clicks a retry button
  }, [
    setCreditCardProcessingFee,
    setCreditCardProcessingFeePercentage,
    setError,
    setLoading,
    total,
    isSurchargeUiEnabled,
    retryCounter,
    setHasCheckedAtLeastOnce,
  ]);

  return {
    loading,
    creditCardProcessingFee,
    creditCardProcessingFeePercentage,
    totalWithFees,
    error,
    triggerRetry,
    hasCheckedAtLeastOnce,
  };
};
